import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  withStyles,
} from '@mui/material';
import { ResourceDto } from '@lmt/shared-types';
import { ScrollToTop } from '@lmt/unicorn-shared/app/components/common/table/helper';
import clsx from 'clsx';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import React, { FC, useEffect, useRef } from 'react';
import { TableDotMenu } from '../../../common/table/TableDotMenu';

import styles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';

type Props = {
  noResults: boolean;
  data: ResourceDto[];
  onDelete(id: ResourceDto): void;
  onEdit(resource: ResourceDto): void;
  translationScope:
    | 'resources.place.list'
    | 'resources.special_conditions.list'
    | 'resources.doctors.list';
};

const nameof = nameofFactory<ResourceDto>();

const columnStyles = {
  [nameof('name')]: {
    minWidth: '200px',
    width: '30%',
    sortable: false,
  },
  ['actions']: {
    minWidth: '10px',
    width: '0.1%',
  },
};

export const ResourcesTable: FC<Props> = React.memo(
  function ResourcesTable(props) {
    const i18n = useScopedTranslation(props.translationScope);

    const claims = useClaims();

    const tableRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (tableRef.current !== null) {
        ScrollToTop(tableRef.current);
      }
    }, [props.data]);

    if (props.noResults) {
      return (
        <div className={styles.emptyList}>
          <div className={styles.emptyListText}>{i18n.t('empty')}</div>
        </div>
      );
    }

    return (
      <TableContainer
        ref={tableRef}
        className={styles.tableContainer}
        component={Paper}
        data-test-id="app-table"
      >
        <Table stickyHeader aria-label="sticky table" className={styles.table}>
          <TableHead>
            <TableRow>
              {Object.keys(columnStyles).map((columnKey) => (
                <TableCell
                  key={`${columnKey}`}
                  onClick={() => columnStyles[columnKey].sortable}
                  className={clsx(styles.header, styles.headerCell)}
                  style={columnStyles[columnKey]}
                >
                  {i18n.t(`column_${columnKey}` as any)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data.map((resource) => (
              <TableRow
                key={resource.id}
                hover
                onClick={
                  claims.permissions.manageResources
                    ? () => props.onEdit(resource)
                    : undefined
                }
              >
                <TableCell>
                  <div className={styles.tableCell}>{resource.name}</div>
                </TableCell>
                <TableCell
                  className={styles.optionCell}
                  onClick={(event: any) => event.stopPropagation()}
                >
                  <TableDotMenu
                    options={[
                      {
                        title: i18n.t('remove_button'),
                        onClick: () => props.onDelete(resource),
                        isDeleteItem: true,
                        isHidden: !claims.permissions.manageResources,
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  },
);
